import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Settings, ArrowLeft, Activity, HeartPulse, Save } from "lucide-react";
import { toast } from "sonner";

export default function AdminConfigPage() {
  const [cfg, setCfg] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [saving, setSaving] = useState(false);
  const [health, setHealth] = useState(null);

  const load = async () => {
    try {
      const [{ data }, h] = await Promise.all([
        api.get("/admin/config"),
        api.get("/admin/health"),
      ]);
      setCfg(data.config);
      setDefaults(data.defaults);
      setHealth(h.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/config", cfg);
      setCfg(data.config);
      toast.success("Configuración guardada");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return <div className="min-h-[60vh] grid place-items-center text-slate-500">Cargando…</div>;

  return (
    <div className="bg-slate-50 min-h-[80vh]">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Link to="/admin" className="inline-flex items-center text-blue-700 hover:text-blue-900 text-sm font-medium mb-3">
          <ArrowLeft className="w-4 h-4 mr-1" /> Volver al panel
        </Link>

        <div className="flex items-start gap-3 mb-6">
          <div className="h-12 w-12 rounded-xl bg-blue-700 text-white grid place-items-center shadow">
            <Settings className="w-6 h-6" />
          </div>
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold text-blue-950">Configuración y monitoreo</h1>
            <p className="text-slate-600 mt-1 text-base">Parámetros operativos en vivo y estado del sistema.</p>
          </div>
        </div>

        {/* Health */}
        <Card className="p-5 rounded-2xl border border-slate-200 bg-white mb-5">
          <div className="flex items-center gap-3 mb-3">
            <Activity className="w-5 h-5 text-emerald-600" />
            <h2 className="font-display text-lg font-semibold text-slate-900">Salud del sistema</h2>
            <span className={`ml-auto text-xs px-2 py-1 rounded-full ${health?.db_ok ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`} data-testid="admin-db-status">
              {health?.db_ok ? "DB OK" : "DB Error"}
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {health && Object.entries(health.counts).map(([k, v]) => (
              <div key={k} className="rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid={`admin-count-${k}`}>
                <div className="text-xs text-slate-500 capitalize">{k.replace("_", " ")}</div>
                <div className="text-xl font-semibold text-slate-900">{v}</div>
              </div>
            ))}
          </div>
        </Card>

        {/* Config */}
        <Card className="p-5 rounded-2xl border border-slate-200 bg-white">
          <div className="flex items-center gap-3 mb-1">
            <HeartPulse className="w-5 h-5 text-blue-600" />
            <h2 className="font-display text-lg font-semibold text-slate-900">Parámetros operativos</h2>
          </div>
          <p className="text-sm text-slate-500 mb-4">Ajusta el funcionamiento de la app en tiempo real. Los cambios aplican de inmediato.</p>

          <div className="space-y-4">
            <ConfigRow
              label="Aprobación automática de publicaciones"
              hint="Si está activada, cada nueva publicación se aprueba sin pasar por revisión."
              control={
                <Switch
                  checked={!!cfg.auto_approve_listings}
                  onCheckedChange={(v) => setCfg({ ...cfg, auto_approve_listings: v })}
                  data-testid="config-auto-approve"
                />
              }
            />
            <ConfigRow
              label="Tamaño máximo por foto (KB)"
              hint={`Por defecto: ${defaults.max_photo_size_kb} KB. Cada publicación tiene 4 fotos.`}
              control={
                <Input
                  type="number"
                  min={50}
                  max={4096}
                  value={cfg.max_photo_size_kb}
                  onChange={(e) => setCfg({ ...cfg, max_photo_size_kb: parseInt(e.target.value || "0", 10) })}
                  className="h-11 w-32"
                  data-testid="config-photo-size"
                />
              }
            />
            <ConfigRow
              label="Publicaciones máx. por usuario"
              hint={`Por defecto: ${defaults.max_listings_per_user}. El admin no tiene límite.`}
              control={
                <Input
                  type="number"
                  min={1}
                  max={1000}
                  value={cfg.max_listings_per_user}
                  onChange={(e) => setCfg({ ...cfg, max_listings_per_user: parseInt(e.target.value || "0", 10) })}
                  className="h-11 w-32"
                  data-testid="config-max-listings"
                />
              }
            />
            <ConfigRow
              label="Tamaño de página del marketplace"
              hint={`Por defecto: ${defaults.listing_page_size}. Afecta cuántas tarjetas se muestran por página.`}
              control={
                <Input
                  type="number"
                  min={6}
                  max={100}
                  value={cfg.listing_page_size}
                  onChange={(e) => setCfg({ ...cfg, listing_page_size: parseInt(e.target.value || "0", 10) })}
                  className="h-11 w-32"
                  data-testid="config-page-size"
                />
              }
            />
          </div>

          <div className="mt-6 flex gap-3 justify-end">
            <Button variant="outline" onClick={load}>Restablecer</Button>
            <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="config-save-btn">
              <Save className="w-4 h-4 mr-2" /> {saving ? "Guardando…" : "Guardar"}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}

function ConfigRow({ label, hint, control }) {
  return (
    <div className="flex items-start gap-4 py-2">
      <div className="flex-1">
        <Label className="text-sm font-medium text-slate-900">{label}</Label>
        <p className="text-xs text-slate-500 mt-0.5">{hint}</p>
      </div>
      <div className="shrink-0">{control}</div>
    </div>
  );
}
